import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchDistricts,
  createUser,
  clearError,
  clearSuccess,
} from "@/store/slices/usersSlice";
import { useNavigate, Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { toast } from "@/hooks/use-toast"; 
import { MultiSelect } from "../../components/ui/multiselect";

const AddUser = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { districts, loading, error, success } = useSelector(
    (state) => state.users
  );

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    designation: "",
    role: "district_admin",
    password: "",
    assigned_districts: [],
  });

  useEffect(() => {
    dispatch(fetchDistricts());
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      toast({
        title: "Success",
        description: success,
        variant: "default",
      });
      navigate("/admin/district_users");
      dispatch(clearSuccess());
    }

    if (error) {
      let errorMsg = error.message || "Failed to create user";

      if (error.errors) {
        const fieldErrors = Object.values(error.errors).flat().join(" - ");
        errorMsg = `${errorMsg}: ${fieldErrors}`;
      } else if (error.error) {
        errorMsg += ` - ${error.error}`;
      }

      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive",
      });

      dispatch(clearError());
    }
  }, [success, error, dispatch, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleDistrictChange = (selectedIds) => {
    setFormData({ ...formData, assigned_districts: selectedIds });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(createUser(formData));
  };
 
  const districtOptions = (districts || []).map((district) => ({
    id: String(district.id),
    name: `${district.name}${district.is_assigned ? " (Already Assigned)" : ""}`,
    disabled: district.is_assigned,
  }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Create District Admin</h1>
        <Link to="/admin/district_users">
          <Button>Back to Users</Button>
        </Link>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="my-5">District Officer Details</CardTitle>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive">
              <AlertDescription>
                {error?.message || error?.error || String(error)}
              </AlertDescription>
            </Alert>
          )}

          <form
            onSubmit={handleSubmit}
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            <div>
              <Label required>Name of District Officer</Label>
              <Input
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>

            <div>
              <Label required>Designation of the District Officer</Label>
              <Input
                name="designation"
                type="text"
                value={formData.designation}
                onChange={handleChange}
                required
              />
            </div>

            <div>
              <Label required>Email</Label>
              <Input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>

            <div>
              <Label required>Phone</Label>
              <Input
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => {
                  const onlyNums = e.target.value.replace(/\D/g, "");
                  if (onlyNums.length <= 10) {
                    setFormData({ ...formData, phone: onlyNums });
                  }
                }}
                pattern="\d{10}"
                maxLength={10}
                placeholder="Enter 10-digit phone number"
                required
              />
            </div>

            <div>
              <Label required>Password</Label>
              <Input
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                required
              />
            </div>

            <div>
              <Label required>Districts Managed by the Officer</Label>
              <MultiSelect
                options={districtOptions}
                value={formData.assigned_districts}
                onChange={handleDistrictChange}
                placeholder="Select Districts"
                disabled={loading}
              />
            </div>

            {/* Buttons */}
            <div className="md:col-span-2 flex justify-end gap-2 mt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  setFormData({
                    name: "",
                    email: "",
                    phone: "",
                    designation: "",
                    password: "",
                    assigned_districts: [],
                  })
                }
              >
                Clear
              </Button>

              <Button type="submit" disabled={loading}>
                {loading ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  "Create District Admin"
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AddUser;